import { useCallback } from 'react';
import { getProjectFetchParamsFromFilters } from '@/utils/filters';
import { fetchProjectsIDs, IProjectsIDsResponse } from '@/directus/services/common/projects-ids/fetch';
import { IFilter } from '../FilteredProjectsGallery.types';
import { TLangIso, TProject, TWorkPageType } from '@/types/global';
import { useFiltersStore } from '@/store/useFiltersStore';
import { IGetProjectsParams } from '@/directus/services/common/projects/types';
import { fetchProjectsClientCached } from '@/directus/services/common/projects/fetch-client';
import { scrollToRef } from '@/utils/generic';

interface UseInitialFilteredProjectsFetchProps {
  // selectedFilters: IFilter[];
  initialProjectsIDs: IProjectsIDsResponse | undefined;
  pageFilters: IFilter[];
  // title: string;
  langIso: TLangIso;
  isUserLogged: boolean;
  workPageType: TWorkPageType;
  updateInitialFetchDisplayedProjects: (isCachedResponse: boolean) => void;
  updateLazyLoadedProjectsLists: () => void;
  fadeInProjectsList: () => void;
  fadeOutProjectsList: () => void;
  fadeInLoaderInitial: () => void;
  fadeOutLoaderInitial: () => void;
}

/* Hook to fetch initial filtered projects based on selected filters and title */
export function useInitialFilteredProjectsFetch({
  // selectedFilters,
  initialProjectsIDs = { notArchived: [], archived: [] },
  pageFilters,
  // title,
  langIso,
  isUserLogged,
  workPageType,
  fadeOutProjectsList,
  fadeInLoaderInitial,
  updateInitialFetchDisplayedProjects,
  updateLazyLoadedProjectsLists,
}: UseInitialFilteredProjectsFetchProps) {


  const computeParamsFetch = useCallback(
    (pageFilters: IFilter[] = []) => {
      const selectedFilters = useFiltersStore.getState().selectedFilters?.filters || [];
      const searchValue = useFiltersStore.getState().search?.value || '';

      const selected = selectedFilters ?? [];
      const filters = [...selected, ...pageFilters];
      return filters.length > 0 || searchValue
        ? getProjectFetchParamsFromFilters({
          filters: filters,
          searchText: searchValue || '',
          isUserLogged: isUserLogged,
          langIso: langIso
        })
        : { lang: langIso };
    },
    [isUserLogged, langIso]
  );

  /* Handle case when no projects are found */
  const handleNoProjectsFound = useCallback(async (isCachedResponse: boolean, paramsToFetch: any) => {
    useFiltersStore.setState({ firstFetchFilteredProjects: [{ projects: [] }] });
    const searchValue = useFiltersStore.getState().search?.value || '';

    // See if there are archived projects
    const allProjectsIDs =
      (await fetchProjectsIDs({
        ...paramsToFetch,
        search: searchValue,
        visibility: ['open', 'private'],
        isArchived: undefined,
      })) || [];

    useFiltersStore.setState({ projectsIDs: allProjectsIDs });

    updateInitialFetchDisplayedProjects(isCachedResponse);
    useFiltersStore.setState({ nextBufferLazyLoadedProjects: [] });
    useFiltersStore.setState({ lazyLoadedProjectsLists: [] });
    // isProjectsLazyLoadFinished will trigger the fetch of archived projects:
    useFiltersStore.getState().setIsProjectsLazyLoadFinished({
      isFinished: true,
      triggerer: 'endLoad',
    });
    updateLazyLoadedProjectsLists();
  }, [
    updateInitialFetchDisplayedProjects,
    updateLazyLoadedProjectsLists
  ]);

  /* Handle case when less than 12 projects are found */
  const handleLessThan12ProjectsFound = useCallback(async (
    projects: TProject[], 
    paramsToFetch: any,
    isCachedResponse: boolean
  ) => {
    useFiltersStore.setState({ firstFetchFilteredProjects: [{ projects }] });
    const searchValue = useFiltersStore.getState().search?.value || '';

    const allProjectsIDs =
      (await fetchProjectsIDs({
        ...paramsToFetch,
        search: searchValue,
        visibility: ['open', 'private'],
        isArchived: undefined,
      })) || [];

    useFiltersStore.setState({ projectsIDs: allProjectsIDs });
    updateInitialFetchDisplayedProjects(isCachedResponse);
    useFiltersStore.setState({ nextBufferLazyLoadedProjects: [] });
    useFiltersStore.setState({ lazyLoadedProjectsLists: [] });
    // isProjectsLazyLoadFinished will trigger the fetch of archived projects:
    useFiltersStore.getState().setIsProjectsLazyLoadFinished({
      isFinished: true,
      triggerer: 'endLoad',
    });
    updateLazyLoadedProjectsLists();
  }, [
    updateInitialFetchDisplayedProjects,
    updateLazyLoadedProjectsLists
  ]);

  /* Handle case when more than 12 projects are found */
  const handleMoreThan12ProjectsFound = useCallback(async (
    initialLoadedProjects: TProject[], 
    paramsToFetch: IGetProjectsParams,
    isCachedResponse: boolean
  ) => {
    const first12Projects = initialLoadedProjects.slice(0, 12);
    useFiltersStore.setState({ firstFetchFilteredProjects: [{ projects: first12Projects }] });
    const searchValue = useFiltersStore.getState().search?.value || '';

    const allProjectsIDs =
      (await fetchProjectsIDs({
        ...paramsToFetch,
        search: searchValue,
        visibility: ['open', 'private'],
        isArchived: undefined,
      })) || [];
      

    useFiltersStore.setState({ projectsIDs: allProjectsIDs });

    // Buffer next 12 projects for lazy loading
    const next12Projects = initialLoadedProjects.slice(12, 24);
    useFiltersStore.setState({ nextBufferLazyLoadedProjects: next12Projects });
    const currentPagination = useFiltersStore.getState().paginationLazyLoad;
    useFiltersStore.setState({ paginationLazyLoad: currentPagination + 1 });
    updateInitialFetchDisplayedProjects(isCachedResponse);
  }, [
    updateInitialFetchDisplayedProjects,
  ]);

  /* Main function to fetch initial filtered projects when filters or title change */
  const fetchInitialFilteredProjects = useCallback(async () => {    
    const elFiltersContainer = document.getElementById('filters-container');
    scrollToRef(elFiltersContainer);
    const isFetching = useFiltersStore.getState().isCurrentlyFetchingProjects;
    const isLazyLoadFinished = useFiltersStore.getState().isProjectsLazyLoadFinished.isFinished;

    if (isFetching || isLazyLoadFinished) return;

    useFiltersStore.getState().setIsArchivedProjFetchFinished(false);
    const selectedFilters = useFiltersStore.getState().selectedFilters;
    const searchValue = useFiltersStore.getState().search?.value || '';

    // In this case we do not want to fetch projects if there are no filters or title, show the initial list
    if ((!selectedFilters?.filters || selectedFilters?.filters?.length === 0) && !searchValue) {
      // Set undefined so initial list is shown
      useFiltersStore.setState({ firstFetchFilteredProjects: undefined });
      useFiltersStore.setState({ projectsIDs: initialProjectsIDs });
      // Set to false becase we start loading initial list, but in work there are no buffered projects
      useFiltersStore.setState({ 
        isProjectsLazyLoadFinished: {
          isFinished: workPageType === 'work' ? true : false,
          triggerer: 'initialFetch',
        },
      });
      return;
    }

    // Fetch projects based on filters and title
    try {
      useFiltersStore.getState().setIsProjectsLazyLoadFinished({
        isFinished: false,
        triggerer: 'initialFetch',
      });
      useFiltersStore.setState({ isCurrentlyFetchingProjects: true });
      useFiltersStore.setState({ paginationLazyLoad: 1 });

      const paramsToFetch = computeParamsFetch(pageFilters);

      // reset buffered projects:
      useFiltersStore.getState().restartLazyLoadedProjectsLists();

      // hide the projects list and show the loader
      fadeOutProjectsList();
      fadeInLoaderInitial();

      const respInitialLoadedProjects = await fetchProjectsClientCached({
        ...paramsToFetch,
        lang: langIso as TLangIso,
        search: searchValue,
        visibility: ['open', 'private'],
        page: 1,
        count: 24,
        isArchived: false,
      });


      const isCachedResponse = respInitialLoadedProjects._fromCache;

      if (!respInitialLoadedProjects?.projects?.length) {
        handleNoProjectsFound( isCachedResponse, paramsToFetch );
      }

      else if (respInitialLoadedProjects.projects.length <= 12) {
        await handleLessThan12ProjectsFound(respInitialLoadedProjects.projects, paramsToFetch, isCachedResponse);
        return;
      }

      else handleMoreThan12ProjectsFound(respInitialLoadedProjects.projects, paramsToFetch, isCachedResponse);

    } finally {
      useFiltersStore.setState({ isCurrentlyFetchingProjects: false });
    }
  }, [
    langIso, 
    pageFilters, 
    computeParamsFetch, 
    fadeInLoaderInitial, 
    fadeOutProjectsList, 
    initialProjectsIDs, 
    workPageType,
    handleLessThan12ProjectsFound,
    handleMoreThan12ProjectsFound,
    handleNoProjectsFound
  ]);

  return { fetchInitialFilteredProjects };
}